import { type FC, useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';

// MUI
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

// common
import { HvsCircleLogo } from 'svg';

// screen
import { ClientModel } from 'modules/map/types';
import { TemplateSettings } from 'modules/map/components/daily-dish/types';
import { getFontFamily } from 'modules/map/components/daily-dish/templates/utils';
import { KIOS_STORAGE } from 'api/API_App';

interface TemplateHeaderProps {
  clientData: ClientModel;
  settings: TemplateSettings;
  weekTitle: string;
  fromDate: string;
  toDate: string;
}

const DEV_STORAGE_HOST = 'kios-dev.huongvietsinh.com';
const PROD_STORAGE_HOST = 'kios.huongvietsinh.com';

/**
 * Storage dev hay thiếu file logo dù cùng path vẫn có trên storage chính.
 * Không gắn crossOrigin: storage chính không gửi Access-Control-Allow-Origin, trình duyệt sẽ huỷ ảnh.
 */
const clientLogoSrc = (logo?: string | null): string | null => {
  const raw = logo?.trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = (KIOS_STORAGE || '').replace(/\/+$/, '');
  const path = raw.replace(/^\/+/, '').replace(/^storage\//, '');
  return base ? `${base}/${path}` : path;
};

const TemplateHeader: FC<TemplateHeaderProps> = (props) => {
  const { clientData, settings, weekTitle, fromDate, toDate } = props;

  const [isCapturing, setIsCapturing] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const logoUrl = clientLogoSrc(clientData.logo);
  const [logoSrc, setLogoSrc] = useState<string | null>(logoUrl);

  // Listen for html2canvas capture events
  useEffect(() => {
    const handleCaptureStart = () => setIsCapturing(true);
    const handleCaptureEnd = () => setIsCapturing(false);

    window.addEventListener('html2canvas-capture-start', handleCaptureStart);
    window.addEventListener('html2canvas-capture-end', handleCaptureEnd);

    return () => {
      window.removeEventListener('html2canvas-capture-start', handleCaptureStart);
      window.removeEventListener('html2canvas-capture-end', handleCaptureEnd);
    };
  }, []);

  const iconCommonStyle = (showLogo: boolean) => {
    return {
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '180px',
      visibility: showLogo ? 'visible' : 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    };
  };

  const iconHeight = 160;

  useEffect(() => {
    setLogoFailed(false);
    setLogoSrc(logoUrl);
  }, [logoUrl]);

  const handleLogoError = () => {
    if (logoSrc?.includes(DEV_STORAGE_HOST)) {
      setLogoSrc(logoSrc.replace(DEV_STORAGE_HOST, PROD_STORAGE_HOST));
      return;
    }
    setLogoFailed(true);
  };

  return (
    <Box
      sx={{
        position: 'relative',
        zIndex: 10,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '180px',
        mt: 2,
      }}
    >
      {/* Client Logo - Absolute positioned on the left */}
      <Box
        sx={{
          ...iconCommonStyle(settings.general.showLogo.client),
          left: 'calc(100% / 4 - 10%)',
        }}
      >
        {logoSrc && !logoFailed ? (
          <img
            alt="Logo khách hàng"
            src={logoSrc}
            onError={handleLogoError}
            style={{
              height: `${iconHeight}px`,
              width: `${iconHeight}px`,
              objectFit: 'contain',
            }}
          />
        ) : null}
      </Box>

      {/* Center Content */}
      <Box
        sx={{
          flex: '0 0 auto',
          maxWidth: 'calc(100% - 400px)',
          p: 2,
          // When capturing for PNG, use solid background; otherwise use glass effect
          backgroundColor: isCapturing ? 'rgba(255, 255, 255, 0.85)' : 'rgba(255, 255, 255, 0.75)',
          borderRadius: '8px',
          backdropFilter: isCapturing ? 'none' : 'blur(10px)',
          WebkitBackdropFilter: isCapturing ? 'none' : 'blur(10px)',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
          border: '1px solid rgba(255, 255, 255, 0.5)',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        {/* Tiêu đề tuần */}
        <Typography
          variant="h6"
          sx={{
            fontSize: `${settings.font.weekTitle.fontSize}px`,
            lineHeight: 1.5,
            color: settings.font.weekTitle.fontColor,
            fontFamily: getFontFamily(settings.font.weekTitle.fontFamilyId),
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
          }}
        >
          THỰC ĐƠN BÁN TRÚ {weekTitle}
        </Typography>
        {/* Tiêu đề tuần phụ */}
        <Typography
          variant="caption"
          sx={{
            color: settings.font.subWeekTitle.fontColor,
            fontSize: `${settings.font.subWeekTitle.fontSize}px`,
            lineHeight: `${settings.font.subWeekTitle.fontSize + 6}px`,
            fontWeight: 600,
            fontFamily: getFontFamily(settings.font.subWeekTitle.fontFamilyId),
          }}
        >
          <FormattedMessage id="map.dateRange" values={{ fromDate, toDate }} />
        </Typography>
      </Box>

      {/* HVS Logo - Absolute positioned on the right */}
      <Box
        sx={{
          ...iconCommonStyle(settings.general.showLogo.hvs),
          right: 'calc(100% / 4 - 10%)',
        }}
      >
        <HvsCircleLogo width={iconHeight} height={iconHeight} />
      </Box>
    </Box>
  );
};

export default TemplateHeader;
